import React, { useState } from 'react';
import { useDebouncedAction } from '../../../../Hooks/DebouncedAction';
import { GetAllLeads, OrganizationUserSearch } from '../../../../Services';
import {
  AutocompleteComponent,
  DialogComponent,
  Spinner,
} from '../../../../Components';
import {
  AddSmartProposalLog,
  ShareGeneratedProposal,
} from '../../../../Services/GeneratedProposals';
import {
  getPublicDownloadableLink,
  showError,
  showSuccess,
  WhatsAppMessage,
} from '../../../../Helper';
import { useTranslation } from 'react-i18next';
import { LeadsStatusEnum, TableActions } from '../../../../Enums';
import { isEmpty } from 'lodash';
import ReceiverTypes from '../../../../Views/Home/SmartProposal/Enums/ReceiverTypes';
import { filterContactsHasEmails } from '../../../../Views/Home/SmartProposal/Helper/emailAdressHelpers';

const fillMessage = ({ clientName, link }) => {
  return `Hello ${clientName},
  I’m pleased to share with you your personalized property proposal prepared by Property Shop Investment.
  You can download it here:
  ${link}
  Feel free to reach out if you have any questions or would like to arrange a viewing!`;
};

const ShareDialog = ({
  onClose,
  open,
  activeItem,
  parentTranslationPath,
  actionType,
}) => {
  const { t } = useTranslation(parentTranslationPath);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [usersData, setUsersData] = useState([]);
  const [leadsData, setLeadsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const getSystemUsers = async (value) => {
    const res = await OrganizationUserSearch({
      pageIndex: 0,
      pageSize: 10,
      name: value,
      userStatusId: 2,
    });
    if (!(res && res.status && res.status !== 200)) {
      const localValue = res.result;
      if (localValue?.length > 0) setUsersData(localValue);
    }
  };
  const getLeads = async (value) => {
    const response = await GetAllLeads({
      pageIndex: 1,
      pageSize: 10,
      search: value,
      leadStatus: LeadsStatusEnum.Open.status,
      leadTypes: null,
    });
    if (!(response && response.status && response.status !== 200))
      setLeadsData(response.result);
    else setLeadsData([]);
  };
  const getSystemUsersDebounced = useDebouncedAction(getSystemUsers, {
    delay: 500,
  });

  const getLeadsDebounced = useDebouncedAction(getLeads, { delay: 500 });
  const saveHandler = async (e) => {
    e.preventDefault();
    if (selectedUsers.length === 0 && selectedLeads.length === 0) {
      showError(t('share_error'));
      return;
    }
    let userReceiver;
    let leadReceiver;

    if (actionType === TableActions.whatsappSolid.key) {
      if (!isEmpty(selectedUsers)) {
        if (isEmpty(selectedUsers.whatsAppNumber)) {
          showError(t('shared_whatsapp_error_user'));
          return;
        }
      }
      if (!isEmpty(selectedLeads)) {
        if (isEmpty(selectedLeads.contactWhatsApp)) {
          showError(t('shared_whatsapp_error_lead'));
          return;
        }
      }

      userReceiver = !isEmpty(selectedUsers)
        ? {
            name: selectedUsers.fullName,
            email: selectedUsers.email,
            receiverId: selectedUsers.id,
            proposalReceiverType: 2,
          }
        : null;
      leadReceiver = !isEmpty(selectedLeads)
        ? {
            name: selectedLeads.lead?.contact_name?.name,
            email: selectedLeads.contactEmailAddress,
            receiverId: selectedLeads.leadId,
            proposalReceiverType: 1,
          }
        : null;

      const receivers = [userReceiver, leadReceiver].filter(
        (item) => item !== null
      );

      try {
        await AddSmartProposalLog({
          proposalId: activeItem.proposalId,
          proposalFileId: activeItem.proposalFileId,
          proposalFileName: activeItem.propertyName,
          propertyName: activeItem.propertyName,
          receivers: receivers,
          actionType: 1,
          unitIds: activeItem.proposalUnitsDetails.map((unit) => +unit.unitId),
        });

        const whatsAppUser = {
          fullname: isEmpty(selectedUsers)
            ? selectedLeads.lead?.contact_name?.name
            : selectedUsers.fullName,
          whatsAppNumber: isEmpty(selectedUsers)
            ? selectedLeads.contactWhatsApp
            : selectedUsers.whatsAppNumber,
        };

        const proposalLink = getPublicDownloadableLink(
          activeItem.proposalFileId
        );

        const link = WhatsAppMessage(
          whatsAppUser.whatsAppNumber,
          fillMessage({
            clientName: whatsAppUser.fullname,
            link: proposalLink,
          })
        );
        window.open(link, '_blank');

        showSuccess(t('shared_success'));
        setSelectedUsers([]);
        setUsersData([]);
        setLeadsData([]);
        setSelectedLeads([]);
        onClose();
      } catch (error) {
        showError(error);
      } finally {
        setIsLoading(false);
      }
    }
    if (actionType === TableActions.emailSolid.key) {
      try {
        const userReceiver = selectedUsers.map((item) => ({
          name: item.fullName,
          email: item.email,
          receiverId: item.id,
          proposalReceiverType: 2,
        }));

        const leadReceiver = selectedLeads?.map((item) => ({
          name: item.lead?.contact_name?.name,
          email: item.contactEmailAddress,
          receiverId: item.leadId,
          proposalReceiverType: 1,
        }));
        await ShareGeneratedProposal({
          proposalId: activeItem.proposalId,
          proposalFileId: activeItem.proposalFileId,
          proposalFileName: activeItem.propertyName,
          propertyName: activeItem.propertyName,
          receivers: [...userReceiver, ...leadReceiver],
          actionType: 1,
          unitIds: activeItem.proposalUnitsDetails.map((unit) => +unit.unitId),
        });
        showSuccess(t('shared_success'));
        setSelectedUsers([]);
        setUsersData([]);
        setLeadsData([]);
        setSelectedLeads([]);
        onClose();
      } catch (error) {
        showError(error);
      } finally {
        setIsLoading(false);
      }
    }
  };
  if (!open) return null;

  return (
    <DialogComponent
      isOpen
      onCloseClicked={() => {
        setSelectedLeads([]);
        setSelectedUsers([]);
        onClose();
      }}
      parentTranslationPath={parentTranslationPath}
      titleText={'Share'}
      maxWidth={'sm'}
      dialogContent={
        <>
          <Spinner
            isActive={isLoading}
            isAbsolute
          />
          <AutocompleteComponent
            parentTranslationPath={parentTranslationPath}
            idRef='System-usersRef'
            inputPlaceholder='select_users'
            selectedValues={selectedUsers}
            labelValue='send_to_user'
            wrapperClasses=' py-2 mb-3'
            data={usersData}
            chipsLabel={(option) => option.fullName}
            withoutSearchButton
            multiple={actionType !== TableActions.whatsappSolid.key}
            isDisabled={
              actionType === TableActions.whatsappSolid.key
                ? !isEmpty(selectedLeads)
                : false
            }
            displayLabel={(option) => option.fullName}
            renderOption={(option) =>
              (option.userName || option.fullName) &&
              `${option.fullName} (${option.userName})`
            }
            onChange={(_, newValue) => {
              setSelectedUsers(newValue);
            }}
            onOpen={() => {
              if (!usersData.length) getSystemUsers();
            }}
            onInputKeyUp={(e) => {
              const { value } = e.target;
              getSystemUsersDebounced(value);
            }}
          />
          <AutocompleteComponent
            parentTranslationPath={parentTranslationPath}
            idRef='System-LeadsRef'
            inputPlaceholder='select_leads'
            labelValue='send_to_lead'
            selectedValues={selectedLeads}
            wrapperClasses=' py-2 mb-3'
            data={leadsData}
            chipsLabel={(option) =>
              option?.lead?.contact_name?.name || option?.lead?.company_name
            }
            withoutSearchButton
            multiple={actionType !== TableActions.whatsappSolid.key}
            isDisabled={
              actionType === TableActions.whatsappSolid.key
                ? !isEmpty(selectedUsers)
                : false
            }
            displayLabel={(option) =>
              option?.lead?.contact_name?.name || option?.lead?.company_name
            }
            renderOption={(option) =>
              option?.lead?.contact_name?.name || option?.lead?.company_name
            }
            onChange={(_, newValue) => {
              const receiverLeads = filterContactsHasEmails(
                newValue,
                ReceiverTypes.Lead.value
              );
              setSelectedLeads(receiverLeads);
            }}
            onOpen={() => {
              if (!leadsData.length) getLeads();
            }}
            onInputKeyUp={(e) => {
              const { value } = e.target;
              getLeadsDebounced(value);
            }}
          />
        </>
      }
      onSaveClicked={saveHandler}
      saveText={'Share'}
      onCancelClicked={() => {
        setSelectedLeads([]);
        setSelectedUsers([]);
        onClose();
      }}
    />
  );
};
export default ShareDialog;
